import {animate, provider} from '@sail/engine';
import {useContext, createContext, useState, useMemo} from 'react';

type SheetStackContext = {
  registerChild(): void;
  unregisterChild(): void;
  parentContext?: SheetStackContext;
  hasModalAncestor: boolean;
};

export const SheetStackContext = createContext<SheetStackContext | undefined>(
  undefined,
);

export const useNestedSheets = (modal: boolean) => {
  const parentContext = useContext(SheetStackContext);
  const [childCount, setChildCount] = useState(0);

  const context = useMemo<SheetStackContext>(
    () => ({
      registerChild() {
        setChildCount((c) => c + 1);
      },
      unregisterChild() {
        setChildCount((c) => c - 1);
      },
      parentContext,
      hasModalAncestor: parentContext?.hasModalAncestor || modal,
    }),
    [parentContext, modal],
  );

  animate.useTimelineListeners(
    useMemo(
      () => ({
        in() {
          parentContext?.registerChild();
        },

        out() {
          parentContext?.unregisterChild();
        },
      }),
      [parentContext],
    ),
  );

  return {
    sheetStackProvider: provider(SheetStackContext, context),
    hasChildSheets: childCount > 0,
    isFirstModalSheet: !parentContext?.hasModalAncestor && modal,
  };
};
